import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { analytics } from '@heycatch/sdk';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('session_token') || null);
  const sessionExpiredToastShown = useRef(false);

  // Session expiry handler - shows toast only once per expiry
  const handleSessionExpired = useCallback(() => {
    if (!sessionExpiredToastShown.current && localStorage.getItem('session_token')) {
      sessionExpiredToastShown.current = true;
      toast.error('Your session has expired. Please log in again.', {
        duration: 5000,
        id: 'session-expired'
      });
    }
    localStorage.removeItem('session_token');
    setToken(null);
    setUser(null);
  }, []);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('session_token');
    setToken(storedToken);
    
    if (!storedToken) {
      setLoading(false);
      return;
    }
    
    const fetchMe = () => axios.get(`${API}/auth/v2/me`, {
      withCredentials: true,
      headers: { 'Authorization': `Bearer ${storedToken}` }
    });
    try {
      let response;
      for (let attempt = 0; ; attempt++) {
        try {
          response = await fetchMe();
          break;
        } catch (err) {
          // Retry transient failures (rate limit, server, network) before treating the user as signed out
          const status = err.response?.status;
          if (status === 401 || (status && status < 429) || attempt >= 2) throw err;
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      setUser(response.data);
      sessionExpiredToastShown.current = false; // Reset on successful auth
    } catch (error) {
      // Check if it's an auth error (401)
      if (error.response?.status === 401) {
        handleSessionExpired();
      } else {
        // For other errors, just clear user state but don't show expiry toast
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  // Link analytics to the signed-in person once per user change
  useEffect(() => {
    if (!user?.user_id) return;
    analytics.setIdentity(
      user.user_id,
      { email: user.email, name: user.name, plan: user.subscription_tier || user.plan },
      { signup_date: user.created_at },
    );
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Setup axios interceptor to catch 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only handle 401 for authenticated routes (not login/signup)
        if (error.response?.status === 401 && localStorage.getItem('session_token')) {
          const requestUrl = error.config?.url || '';
          // Don't show session expired for login/signup/public endpoints
          if (!requestUrl.includes('/auth/v2/login') && 
              !requestUrl.includes('/auth/v2/signup') &&
              !requestUrl.includes('/auth/login') && 
              !requestUrl.includes('/auth/register') && 
              !requestUrl.includes('/api/admin/') &&
              !requestUrl.includes('/public/')) {
            handleSessionExpired();
          }
        }
        const PUBLIC_PATHS = ['/', '/login', '/pricing', '/forgot-password', '/reset-password', '/complete-payment', '/signup'];
        const onPublic = PUBLIC_PATHS.some((p) => p === '/' ? window.location.pathname === '/' : window.location.pathname.startsWith(p));
        if (error.response?.status === 402 && error.response?.data?.code === 'payment_required' && !onPublic) {
          window.location.assign('/complete-payment');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleSessionExpired]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/v2/logout`, {}, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setToken(null);
    setUser(null);
    analytics.resetIdentity();
    sessionExpiredToastShown.current = false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      token, 
      setToken, 
      loading, 
      checkAuth, 
      logout,
      handleSessionExpired,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
